import React, { useMemo, useState } from "react";

import clsx from "clsx";
import { useLocation } from "react-router-dom";

import {
  type AugmentedFactColumn,
  VirtualizedTable,
} from "@components/reports/virtualized-table";
import { useUser } from "@components/user-context";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  type IGranularity,
  type Breakdown,
  type CategoricalBreakdownConfig,
  type SalesReportRow,
  type Table,
  type DateInterval,
  type PartialBy,
} from "@north-beam/nb-common";
import { flatten } from "@north-beam/nb-common";

import { useReportBodyState } from "../report-body-control";

import { createDimensionColumns, createFactColumns } from "./column-builder";
import { ColumnOrderModal } from "./column-order-modal";
import { ExportToCsvModal } from "./export-to-csv-modal";
import { TableSearchFilter } from "./table-search-filter";
import {
  preprocessForTable,
  createDailyDataRoot,
  createNativeTableRoot,
} from "./table-utils";
import {
  useColumnOrdering,
  COLUMN_ORDERING_STRING_FOR_SALES_PAGE_PRIMARY_TABLE,
  HACK_columnMatching,
} from "./use-column-ordering";

interface TableSectionProps {
  rows: SalesReportRow[];
  breakdownConfigs: CategoricalBreakdownConfig[];
  metricsArray: SalesPageMetric[];

  // Because this table is being used in other places
  breakdowns?: Breakdown[];
  dateRange?: DateInterval;
  compareDateRange?: DateInterval;
  granularity?: IGranularity;
}

export const TableSection = (props: PartialBy<TableSectionProps, "rows">) => {
  const { rows = [], breakdownConfigs, metricsArray } = props;
  const [textFilter, setTextFilter] = useState("");
  const {
    breakdowns: breakdownsState,
    dateRange: dateRangeState,
    compareDateRange: compareDateRangeState,
    granularity: granularityState,
  } = useReportBodyState();

  const breakdowns = props.breakdowns || breakdownsState;
  const dateRange = props.dateRange || dateRangeState;
  const compareDateRange = props.compareDateRange || compareDateRangeState;
  const granularity = props.granularity || granularityState;

  const [data, dailyData] = useMemo(
    () =>
      preprocessForTable(
        rows,
        breakdownConfigs,
        breakdowns,
        dateRange,
        compareDateRange,
        textFilter,
      ) ?? null,
    [
      rows,
      breakdownConfigs,
      breakdowns,
      dateRange,
      compareDateRange,
      textFilter,
    ],
  );
  const { user } = useUser();
  const { search } = useLocation();
  const { columnOrdering, columnOrderingLoading, setColumnOrdering } =
    useColumnOrdering();

  const [columnOrderModalOpen, setColumnModelOpen] = useState<boolean>(false);
  const [exportToCsvModalOpen, setExportToCsvModalOpen] = useState(false);
  const extraExportDimensionColumns: Set<string> = useMemo(() => new Set(), []);

  const dailyDataRoot = useMemo(
    () =>
      createDailyDataRoot(dailyData, extraExportDimensionColumns, metricsArray),
    [dailyData, metricsArray, extraExportDimensionColumns],
  );

  const nativeTableRoot = useMemo(
    () =>
      createNativeTableRoot(
        data,
        extraExportDimensionColumns,
        metricsArray,
        search,
        user,
      ),
    [data, metricsArray, search, extraExportDimensionColumns, user],
  );

  const dimensionColumn: AugmentedFactColumn = useMemo(
    () => createDimensionColumns(user, breakdowns, granularity),
    [user, breakdowns, granularity],
  );

  const factColumns = useMemo(
    () => createFactColumns(metricsArray),
    [metricsArray],
  );

  const root = useMemo(() => {
    if (!nativeTableRoot) {
      return null;
    }

    const rv = { ...nativeTableRoot };
    rv.children = [];
    return rv;
  }, [nativeTableRoot]);

  const preppedRows = useMemo(
    () => nativeTableRoot && [...nativeTableRoot.children],
    [nativeTableRoot],
  );

  const table: Table | null = useMemo(() => {
    if (!nativeTableRoot) {
      return null;
    }
    return {
      title: "",
      dimensionColumns: [
        ...breakdowns.map((v) => ({ name: v.key, key: v.key })),
        dimensionColumn,
      ],
      factColumns,
      rows: flatten(nativeTableRoot),
    };
  }, [breakdowns, dimensionColumn, factColumns, nativeTableRoot]);

  const formattedExtraExportDimensionsColumns = [
    ...extraExportDimensionColumns,
  ].map((key) => ({ name: key, key }));

  const dailyDataTable = useMemo(() => {
    if (!dailyDataRoot) {
      return null;
    }

    return {
      title: "",
      dimensionColumns: [
        { name: "Date", key: "Date" },
        ...breakdowns.map((v) => ({ name: v.key, key: v.key })),
        dimensionColumn,
        ...formattedExtraExportDimensionsColumns,
      ],
      factColumns,
      rows: dailyDataRoot,
    };
  }, [
    breakdowns,
    dimensionColumn,
    factColumns,
    dailyDataRoot,
    formattedExtraExportDimensionsColumns,
  ]);

  return (
    <div
      className={clsx("card p-4 mt-4", {
        "waiting-interstitial": columnOrderingLoading,
      })}
    >
      <div className="flex items-center justify-between mb-4">
        <TableSearchFilter
          textFilter={textFilter}
          setTextFilter={setTextFilter}
        />
        <div>
          <ColumnOrderModal
            setColumnModelOpen={setColumnModelOpen}
            columnOrderModalOpen={columnOrderModalOpen}
            columnOrdering={columnOrdering}
            columns={factColumns}
            setColumnOrdering={(columnOrdering) => {
              setColumnOrdering({
                variables: {
                  columnOrdering,
                  tableName:
                    COLUMN_ORDERING_STRING_FOR_SALES_PAGE_PRIMARY_TABLE,
                },
              });
            }}
          />
          <ExportToCsvModal
            table={{
              ...table,
              dimensionColumns: [
                ...(table?.dimensionColumns ?? []),
                ...formattedExtraExportDimensionsColumns,
              ],
            }}
            dailyDataTable={dailyDataTable}
            exportToCsvModalOpen={exportToCsvModalOpen}
            setExportToCsvModalOpen={setExportToCsvModalOpen}
          />
        </div>
      </div>
      <div style={{ paddingBottom: "300px" }} data-test="sales-table">
        {data && root ? (
          <VirtualizedTable
            rowHeight={50}
            columnWidth={110}
            frozenRows={[root]}
            rows={preppedRows}
            dimensionColumn={dimensionColumn}
            factColumns={factColumns}
            allowSortingByLastDimensionColumn={true}
            columnOrdering={columnOrdering}
            HACK_columnMatching={HACK_columnMatching}
          />
        ) : (
          <div className="card">
            <div className="card-body">
              No performance data for this report.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
